import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { CareCreditCollectMultiStep, CareCreditCollectSubmissionStatus } from '@frontend/payments-care-credit';
import { getPersonActiveInvoiceKey } from '@frontend/person-invoice-list';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useUpdateRequestStatusPoll } from '../../../hooks/use-update-request-status-poll';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CareCreditCollectStepBodyProps } from './care-credit-collect.types';

export const CareCreditCollectBody = ({
  onGoBack,
  onCancel,
  onSubmissionAccepted,
  onSubmissionDeclined,
  onGoToMessageStep,
}: CareCreditCollectStepBodyProps) => {
  const onComplete = useCallback(() => {
    onGoBack();
  }, [onGoBack]);

  const {
    person,
    invoice,
    careCreditData,
    activeLocationId,
    hideBackButtonOnCareCreditCollect,
    merchant,
    invoiceQuery: { refetch: refetchInvoiceQuery },
  } = useCollectPaymentMultiContext();
  const queryClient = useQueryClient();
  const personActiveInvoiceKey = getPersonActiveInvoiceKey();

  const { setProcessingAndStartPoll } = useUpdateRequestStatusPoll(invoice, () => {
    queryClient.invalidateQueries({
      // this query invalidation is useful when a user completes a CC payment
      // in office, then the payment requests for that user get updated in the
      // contact panel, and also the CC touch points in the inbox (thread header
      // and message template area)
      predicate: ({ queryKey }) => {
        return (
          queryKey.includes(personActiveInvoiceKey[0]) &&
          queryKey.includes(person?.personId || 'value-that-will-not-be-matched')
        );
      },
    });
  });

  const onSubmissionComplete = useCallback(
    (status: CareCreditCollectSubmissionStatus) => {
      if (status === 'success') {
        onSubmissionAccepted();
        setProcessingAndStartPoll();
      } else if (status === 'declined') {
        onSubmissionDeclined();
      }
    },
    [onSubmissionAccepted, onSubmissionDeclined]
  );

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoiceQuery().then((res) => res.data);
  }, [refetchInvoiceQuery]);

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditCollect}
      label={PAYMENT_MULTI_STEP_LABELS['care-credit-collect']}
      hasInternalFooter
      showBlueHeader
      hasChildStepper
      components={{
        Footer: () => null,
      }}
    >
      <CareCreditCollectMultiStep
        multiStepProps={{
          onComplete,
        }}
        merchant={merchant}
        locationId={activeLocationId}
        invoice={invoice}
        person={person}
        careCreditData={careCreditData}
        hideBackButtonOnCareCreditCollect={hideBackButtonOnCareCreditCollect}
        onCancel={onCancel}
        onGoBack={onGoBack}
        onGoToMessageStep={onGoToMessageStep}
        onSubmissionComplete={onSubmissionComplete}
        onRefetchInvoice={() => onRefetchInvoice()}
      />
    </StyledStep>
  );
};
